import { useEffect, useState, useCallback } from 'react';
import { getSubscription, cancelSubscription, createSubscription } from '@genfun/core/api/auth';
import { openTabWithUrl } from '@genfun/core/utils/openTabWithUrl';
import './AccountPopup.css';

const TIER_META = {
    1: {
        name: 'Starter',
        price: '$9.99',
        period: '/month',
        features: ['50 tokens / month', '≈ 16 photos or 5 videos', 'No watermark'],
    },
    2: {
        name: 'Premium',
        price: '$25',
        period: '/month',
        features: ['200 tokens / month', '≈ 66 photos or 20 videos', 'Priority queue'],
    },
};

const PLAN_CARDS = [
    {
        tier: 1,
        name: 'Starter',
        price: '$9.99',
        period: '/mo',
        features: ['50 tokens / month', '≈ 16 photos or 5 videos', 'No watermark', 'Cancel anytime'],
    },
    {
        tier: 2,
        name: 'Premium',
        price: '$25',
        strike: '$60',
        period: '/mo',
        popular: true,
        features: ['200 tokens / month', '≈ 66 photos or 20 videos', 'Priority queue', 'Cancel anytime'],
    },
];

const STATUS_META = {
    active:    { label: 'Active',    tone: 'good' },
    cancelled: { label: 'Cancelled', tone: 'warn' },
    past_due:  { label: 'Past due',  tone: 'bad'  },
};

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Centrobill subscriptions in our setup are monthly. We don't get an explicit
// next-billing date from the backend, so we derive it as last_rebill_at + 1 month
// (or started_at + 1 month if there hasn't been a rebill yet). Marked as
// estimated in the UI.
function computeNextBilling(sub) {
    if (!sub) return null;
    const anchor = sub.last_rebill_at || sub.started_at;
    if (!anchor) return null;
    const d = new Date(anchor);
    if (isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + 1);
    return d;
}

function daysUntil(date) {
    if (!date) return null;
    const ms = date.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

// Locks background scroll while the popup is open (body + html overflow,
// compensates for the scrollbar width). Same lock used across d33p popups.
function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
        };
    }, [isOpen]);
}

export function AccountPopup({ isOpen, onClose, token, user, refreshUser, onResetOnboarding }) {
    const [sub, setSub] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [subscribingTier, setSubscribingTier] = useState(null);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!token) { setSub(null); setStatus(null); return; }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await getSubscription(token);
            setSub(data);
            setStatus(code);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useLockBodyScroll(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        refresh();
    }, [isOpen, refresh]);

    const handleSubscribe = (tier) => {
        if (!token || subscribingTier !== null) return;
        setSubscribingTier(tier);
        setError(null);
        openTabWithUrl(() => createSubscription(token, tier).then(r => r.url))
            .catch(e => setError(e.message || String(e)))
            .finally(() => setSubscribingTier(null));
    };

    const handleCancel = async () => {
        if (!confirmCancel) { setConfirmCancel(true); return; }
        setCancelling(true);
        setError(null);
        try {
            const [data, code] = await cancelSubscription(token);
            if (code !== 200) {
                setError((data && data.detail) || 'Failed to cancel');
                return;
            }
            setConfirmCancel(false);
            await refresh();
            refreshUser?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setCancelling(false);
        }
    };

    if (!isOpen) return null;

    const hasSub      = status === 200 && sub;
    const isActive    = hasSub && sub.status === 'active';
    const tier        = hasSub ? TIER_META[sub.tier] || { name: `Tier ${sub.tier}`, price: '', period: '', features: [] } : null;
    const statusMeta  = hasSub ? (STATUS_META[sub.status] || { label: sub.status, tone: 'warn' }) : null;
    const isCancelled = hasSub && sub.status === 'cancelled';
    const nextBilling = hasSub && !isCancelled ? computeNextBilling(sub) : null;
    const accessUntil = hasSub && isCancelled ? computeNextBilling(sub) : null;
    const credits     = user?.credits ?? 0;

    return (
        <div className="fuck-account-overlay" onClick={onClose}>
            <div className="fuck-account-popup" onClick={e => e.stopPropagation()}>
                <button className="fuck-account-close" onClick={onClose} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                <div className="fuck-account-header">
                    <div className="fuck-account-avatar">
                        {(user?.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="fuck-account-identity">
                        <div className="fuck-account-name">My Account</div>
                        <div className="fuck-account-email">{user?.email || '—'}</div>
                    </div>
                </div>

                {hasSub && (
                    <div className="fuck-account-credits-card">
                        <div className="fuck-account-credits-label">Tokens available</div>
                        <div className="fuck-account-credits-value">
                            <span className="fuck-account-credits-num">{credits}</span>
                            <span className="fuck-account-credits-unit">tokens</span>
                        </div>
                        {tier && (
                            <div className="fuck-account-credits-hint">
                                {isActive
                                    ? `Refills with your ${tier.name} plan`
                                    : accessUntil
                                        ? `Use them by ${formatDate(accessUntil.toISOString())}`
                                        : 'Use them before your access ends'}
                            </div>
                        )}
                    </div>
                )}

                <div className="fuck-account-section-title">Subscription</div>

                {loading && !sub && (
                    <div className="fuck-account-loading">Loading…</div>
                )}

                {!loading && status === 404 && (
                    <div className="fuck-account-plans">
                        <div className="fuck-account-plans-intro">
                            Pick a plan — billed monthly, cancel anytime.
                        </div>
                        <div className="fuck-account-plan-cards">
                            {PLAN_CARDS.map(p => (
                                <div
                                    key={p.tier}
                                    className={`fuck-account-plan${p.popular ? ' fuck-account-plan--popular' : ''}`}
                                >
                                    {p.popular && <div className="fuck-account-plan-badge">Best value</div>}
                                    <div className="fuck-account-plan-name">{p.name}</div>
                                    <div className="fuck-account-plan-price">
                                        {p.price}
                                        <span className="fuck-account-plan-per">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="fuck-account-plan-features">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`fuck-account-plan-btn${p.popular ? ' fuck-account-plan-btn--primary' : ''}`}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : 'Buy'}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="fuck-account-plans-foot">
                            Secure checkout via Centrobill.
                        </div>
                    </div>
                )}

                {!loading && hasSub && (
                    <div className="fuck-account-sub-card">
                        <div className="fuck-account-sub-top">
                            <div>
                                <div className="fuck-account-sub-plan">{tier.name}</div>
                                <div className="fuck-account-sub-price">
                                    {tier.price}<span className="fuck-account-sub-period">{tier.period}</span>
                                </div>
                            </div>
                            <span className={`fuck-account-status fuck-account-status--${statusMeta.tone}`}>
                                <span className="fuck-account-status-dot" /> {statusMeta.label}
                            </span>
                        </div>

                        <div className="fuck-account-sub-rows">
                            {sub.started_at && (
                                <div className="fuck-account-sub-row">
                                    <span className="fuck-account-sub-row-label">Started</span>
                                    <span className="fuck-account-sub-row-val">{formatDate(sub.started_at)}</span>
                                </div>
                            )}
                            {sub.last_rebill_at && (
                                <div className="fuck-account-sub-row">
                                    <span className="fuck-account-sub-row-label">Last payment</span>
                                    <span className="fuck-account-sub-row-val">{formatDate(sub.last_rebill_at)}</span>
                                </div>
                            )}
                            {nextBilling && (
                                <div className="fuck-account-sub-row fuck-account-sub-row--accent">
                                    <span className="fuck-account-sub-row-label">Next renewal</span>
                                    <span className="fuck-account-sub-row-val">
                                        {formatDate(nextBilling.toISOString())}
                                        {daysUntil(nextBilling) !== null && daysUntil(nextBilling) >= 0 && (
                                            <span className="fuck-account-sub-row-meta"> · in {daysUntil(nextBilling)} day{daysUntil(nextBilling) === 1 ? '' : 's'}</span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {isCancelled && accessUntil && (
                                <div className="fuck-account-sub-row fuck-account-sub-row--accent">
                                    <span className="fuck-account-sub-row-label">Access until</span>
                                    <span className="fuck-account-sub-row-val">{formatDate(accessUntil.toISOString())}</span>
                                </div>
                            )}
                            {sub.cancelled_at && (
                                <div className="fuck-account-sub-row">
                                    <span className="fuck-account-sub-row-label">Cancelled on</span>
                                    <span className="fuck-account-sub-row-val">{formatDate(sub.cancelled_at)}</span>
                                </div>
                            )}
                        </div>

                        {tier.features?.length > 0 && (
                            <ul className="fuck-account-features">
                                {tier.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        )}

                        {isCancelled ? (
                            <div className="fuck-account-renew">
                                <button
                                    className="fuck-account-renew-btn"
                                    onClick={() => handleSubscribe(sub.tier)}
                                    disabled={subscribingTier !== null}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 4 21 10 15 10"/></svg>
                                    {subscribingTier === sub.tier
                                        ? 'Redirecting…'
                                        : `Renew ${tier.name} — ${tier.price}${tier.period}`}
                                </button>
                                <div className="fuck-account-renew-note">
                                    You keep access until {accessUntil ? formatDate(accessUntil.toISOString()) : 'the period ends'}.
                                </div>
                            </div>
                        ) : (
                            <div className="fuck-account-actions">
                                <button
                                    className={`fuck-account-cancel${confirmCancel ? ' fuck-account-cancel--confirm' : ''}`}
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                >
                                    {cancelling
                                        ? 'Cancelling…'
                                        : confirmCancel
                                            ? 'Click again to confirm cancel'
                                            : 'Cancel subscription'}
                                </button>
                                {confirmCancel && !cancelling && (
                                    <button className="fuck-account-cancel-back" onClick={() => setConfirmCancel(false)}>
                                        Keep subscription
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!loading && hasSub && isCancelled && (
                    <div className="fuck-account-plans fuck-account-plans--inline">
                        <div className="fuck-account-plans-divider">
                            <span>or switch plan</span>
                        </div>
                        <div className="fuck-account-plan-cards">
                            {PLAN_CARDS.filter(p => p.tier !== sub.tier).map(p => (
                                <div
                                    key={p.tier}
                                    className={`fuck-account-plan${p.popular ? ' fuck-account-plan--popular' : ''}`}
                                >
                                    {p.popular && <div className="fuck-account-plan-badge">Best value</div>}
                                    <div className="fuck-account-plan-name">{p.name}</div>
                                    <div className="fuck-account-plan-price">
                                        {p.price}
                                        <span className="fuck-account-plan-per">{p.period}</span>
                                        {p.strike && <s>{p.strike}</s>}
                                    </div>
                                    <ul className="fuck-account-plan-features">
                                        {p.features.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                    <button
                                        className={`fuck-account-plan-btn${p.popular ? ' fuck-account-plan-btn--primary' : ''}`}
                                        onClick={() => handleSubscribe(p.tier)}
                                        disabled={subscribingTier !== null}
                                    >
                                        {subscribingTier === p.tier ? 'Redirecting…' : `Switch to ${p.name}`}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <div className="fuck-account-error">{error}</div>}

                {typeof onResetOnboarding === 'function' && (
                    <div className="fuck-account-dev">
                        <button
                            className="fuck-account-reset-onboarding"
                            onClick={onResetOnboarding}
                        >
                            Reset onboarding
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
